import * as AuditLogSelectos from "../store/selectors/audit-log.selector";

import { Store, select } from "@ngrx/store";
import { Observable, distinctUntilChanged, filter, map, of, switchMap, take, tap, throwError } from "rxjs";
import { AuditLogDb, AuditLogHelper, AuditType, IAuditLogFilterParams } from "../models/audit-log-type.model";

import { Injectable } from "@angular/core";
import { LoadingState } from "@app/root-store/api-result.state";
import { ClareDbCollection } from "@app/shared/resources/resource-objectifier";
import { ILogService } from "@app/shared/services/logs/log.interface";
import { AuditLogDispatcher } from "../store/dispatchers/audit-log.dispatcher";
import { AuditLogDbService } from "./audit-log-db.service";
import { createAuditLogKey } from "./audit-log.constants";

@Injectable({
    providedIn: "root",
})
export class AuditLogService implements ILogService {
    constructor(
        private readonly auditDbService: AuditLogDbService,
        private readonly dispatcher: AuditLogDispatcher,
        private readonly store: Store
    ) {}

    dispatchGetLogTypes() {
        this.dispatcher.getTypes();
    }

    fetchAuditLogTypes() {
        return this.auditDbService.getAuditLogTypes();
    }

    fetchAuditLogSet(params: IAuditLogFilterParams) {
        return this.auditDbService.getAuditLogs(params);
    }

    getAuditLogTypesByCategory(category: string): Observable<AuditType[]> {
        return this.getStoreAuditTypes().pipe(
            map((auditTypeObj: ClareDbCollection<AuditType>) =>
                Object.values(auditTypeObj || {}).filter(auditType => auditType.category.toLowerCase() === category.toLowerCase())
            )
        );
    }

    getStoreAuditTypes() {
        return this.store.pipe(
            select(AuditLogSelectos.getAllAuditTypes()),
            tap(auditTypes => {
                if (auditTypes?.callState === LoadingState.init) {
                    this.dispatchGetLogTypes();
                }
            }),
            filter(auditTypes => auditTypes?.callState === LoadingState.loaded),
            map(auditTypes => auditTypes.result)
        );
    }

    getStoreAuditsState(params: IAuditLogFilterParams) {
        return this.store.pipe(
            select(AuditLogSelectos.getAudits()),
            distinctUntilChanged(),
            tap(auditData => {
                if (auditData.callState === LoadingState.init) {
                    this.dispatcher.getAuditLogs(params);
                }
            }),
            switchMap(auditData => {
                if (auditData.callState === LoadingState.loaded) {
                    return this.getStoreAuditTypes().pipe(
                        take(1),
                        map(auditTypesState => {
                            auditData.result.forEach(audit => {
                                const auditType = auditTypesState[audit.auditTypeId] || null;
                                if (auditType) {
                                    audit.auditType = auditType;
                                    audit.auditTypeDisplay = `${auditType.type} ${auditType.subType}`;
                                    audit.userName = `${audit.firstName || ""} ${audit.lastName || ""}`;
                                }
                            });
                            return auditData;
                        })
                    );
                }

                return of(auditData);
            })
        );
    }

    logEvent(auditName: string, logData: any, isEdit = false) {
        const saveLogEvent = savingLogData => this.auditDbService.logEvent(savingLogData, isEdit).pipe(take(1));
        if (isEdit) {
            return saveLogEvent(logData).subscribe();
        }
        return this.getStoreAuditTypes()
            .pipe(
                take(1),
                switchMap(auditTypes => {
                    const auditType = Object.values(auditTypes).find(
                        at => createAuditLogKey(at.category, at.type, at.subType) === auditName
                    );
                    const log = AuditLogHelper.getAuditLogCreateData(
                        auditType?.id,
                        logData.data,
                        logData.entityId,
                        logData?.optionalParams?.uuid || null,
                        logData.projectId
                    );
                    if (auditType) {
                        return saveLogEvent(log);
                    }

                    return throwError(() => ({ message: "Log saves failed!" }));
                })
            )
            .subscribe(_ => {
                this.clearLogDataState();
            });
    }

    logEventWithoutEndTime(auditName: string, logData: any): Observable<AuditLogDb> {
        return this.getStoreAuditTypes().pipe(
            take(1),
            switchMap(auditTypes => {
                const auditType = Object.values(auditTypes).find(at => createAuditLogKey(at.category, at.type, at.subType) === auditName);
                const log = AuditLogHelper.getAuditLogCreateData(
                    auditType?.id,
                    logData.data,
                    logData.entityId,
                    logData?.optionalParams?.uuid || null,
                    logData.projectId,
                    false
                );
                if (auditType) {
                    return this.auditDbService.addAuditLog(log).pipe(
                        take(1),
                        tap(_ => this.clearLogDataState())
                    );
                }

                return throwError(() => ({ message: "Log save failed!" }));
            })
        );
    }

    clearAuditLogState() {
        this.dispatcher.clearAuditLogState();
    }

    clearLogDataState() {
        this.dispatcher.clearLogDataState();
    }
}
